import { Fragment, useRef, useState } from "react";
import { GrundartikelLink } from "@/components/catalog/GrundartikelLink";
import { Link } from "react-router-dom";
import type { ArbeitsplatzSchrittKey, EinlagerungZeile, PurchaseDocument, PurchaseOrderFile, PurchaseShipment } from "@/api/purchase";
import { useEinlagerung, usePatchPurchaseShipment } from "@/api/purchase";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { docStatusLabel } from "@/lib/purchase-format";
import { mengeMitEinheit } from "../einheit";
import { PackdatenEingabe } from "../PackdatenEingabe";
import { PackstationAnteil } from "../PackstationAnteil";
import { RestAusweisenKnopf } from "../Restmengen";
import { ContainerStatusAktionen } from "../ContainerStatusAktionen";
import { BelegHochladen } from "./Belege";
import {
  GATE_TEXT, KLAERFALL_BESTAND_TEXT, STATUS_TEXTE, codeText, schrittText, statusChipKlasse,
  type IrgendeinSchritt, type Spur,
} from "./schritte";

export const AKTE_REITER = ["positionen", "container", "zahlungen", "verlauf", "dokumente"] as const;
export type AkteReiter = (typeof AKTE_REITER)[number];

export interface ArbeitsplatzRechte {
  canCreateReceipt: boolean;
  hasPushPerm: boolean;
  canBookStock: boolean;
  canConfirmMischEk: boolean;
  hasPricePerm: boolean;
  /** Termine (Avis) aendern und Belege hochladen: dasselbe Recht wie die Akte (article_purchase). */
  canEditAkte: boolean;
  /** Kosten ohne weitere Posten freigeben: admin_general (wie im Reiter Container). */
  darfKostenFreigeben: boolean;
}

/** Formulare der Seitenspalte, die ein Hauptknopf oeffnet (21.10.2026 „alles in der Maske“). */
export type FormularZiel = "invoice" | "zahlung" | "kosten";

export interface ArbeitsplatzAktionen {
  oeffneWe: (shipmentId: number) => void;
  /** Plan 2026-10-19 (a): Schritt 6 „Bestand buchen“ (Plattform, unabhaengig vom DR-Gate). */
  oeffneBestandBuchen: (shipmentId: number) => void;
  oeffneMischEk: (shipmentId: number) => void;
  zeigeReiter: (reiter: AkteReiter) => void;
  fokusStatus: () => void;
  /** Belege hochladen, null = zur Bestellung (ohne Container). Derselbe Lauf wie im Reiter Dokumente. */
  belegeHochladen: (dateien: File[], shipmentId: number | null) => void;
  /** Review-Dialog der Akte fuer einen Beleg (Packliste pruefen und bestaetigen). */
  pruefeBeleg: (documentId: number) => void;
  /** Korrektur-Dialog der Akte (Begruendung Pflicht): Packliste wieder oeffnen, Packdaten-Ausnahme. */
  korrektur: (art: KorrekturArt, id: number) => void;
  /** Dialog „Positionen hinzufügen“ der Akte. */
  oeffnePositionen: () => void;
  /** Dialoge „Dokument ablehnen“ / „Dokument löschen“ der Akte (Grund Pflicht). */
  lehneBelegAb: (documentId: number) => void;
  loescheBeleg: (documentId: number) => void;
}

export type KorrekturArt = "reopen_document" | "carton_exception" | "clear_carton_exception";

interface AufgabeKarteProps {
  spur: Spur;
  schritt: IrgendeinSchritt | null;
  shipment: PurchaseShipment | null;
  order: PurchaseOrderFile;
  titel: string;
  farbe: string;
  rechte: ArbeitsplatzRechte;
  aktionen: ArbeitsplatzAktionen & { oeffneFormular: (ziel: FormularZiel) => void };
  documents: PurchaseDocument[];
  uploadLaeuft: boolean;
}

type Hauptaktion =
  | { art: "knopf"; label: string; onClick: () => void; hinweis?: string }
  | { art: "upload"; label: string; feldLabel: string; shipmentId: number }
  | { art: "hinweis"; text: string }
  | null;

const PACKLISTEN_TYPEN = new Set(["packing_list", "invoice_packing_combo"]);

/** Packlisten-Belege eines Containers (Packliste oder Rechnung+Packliste). */
function packlistenVon(documents: PurchaseDocument[], shipmentId: number): PurchaseDocument[] {
  return documents.filter((d) => d.purchase_shipment_id === shipmentId && d.typ !== null && PACKLISTEN_TYPEN.has(d.typ));
}

/** Packdaten und Packstation-Anteil sind nach Zustellung, WE oder Archiv gesperrt (wie im Reiter Container). */
function packdatenGesperrt(order: PurchaseOrderFile, shipment: PurchaseShipment): boolean {
  return Boolean(order.archived_at) || shipment.warehouse_receipt_id !== null
    || ["zugestellt", "im_wareneingang", "eingelagert"].includes(shipment.status);
}

/** Fokus auf das erste Ziel in der Karte; ohne Ziel zurueck in den Reiter der Akte. */
type Fokus = (ziel: string, reiter: AkteReiter) => void;

/** Rechte-Matrix aus dem Plan: ein Hauptknopf je Schritt, sonst der Satz, wer zuständig ist. */
function hauptaktion(
  schritt: IrgendeinSchritt, shipment: PurchaseShipment | null, rechte: ArbeitsplatzRechte, aktionen: AufgabeKarteProps["aktionen"],
  ctx: { titel: string; documents: PurchaseDocument[]; fokus: Fokus; drPushEnabled: boolean },
): Hauptaktion {
  const codes = schritt.codes.map((c) => c.code);
  const sid = shipment?.id ?? null;
  // Rechnung, Zahlung und Kosten schreiben nur Preisrechte; der Knopf oeffnet das Formular daneben.
  const formular = (label: string, ziel: FormularZiel): Hauptaktion => rechte.hasPricePerm
    ? { art: "knopf", label, onClick: () => aktionen.oeffneFormular(ziel) }
    : { art: "hinweis", text: "Zuständig: Admin/Preise" };
  switch (schritt.key) {
    case "packliste": {
      // Ausgelesene Packliste: direkt pruefen und bestaetigen; sonst hochladen (mit Auswertung).
      if (sid === null) return null;
      if (!rechte.canEditAkte) return { art: "hinweis", text: "Zuständig: Einkauf" };
      const offen = packlistenVon(ctx.documents, sid).find((d) => d.agent_status === "extrahiert");
      if (offen) return { art: "knopf", label: "Packliste prüfen und bestätigen", onClick: () => aktionen.pruefeBeleg(offen.id) };
      return { art: "upload", label: "Packliste hochladen", feldLabel: `Packliste für ${ctx.titel} auswählen`, shipmentId: sid };
    }
    case "packdaten":
      return { art: "knopf", label: "Packdaten prüfen", onClick: () => ctx.fokus("packdaten", "container") };
    case "transport":
      return null;
    case "ankunft":
      if (schritt.status === "fix") return { art: "knopf", label: "Packdaten prüfen", onClick: () => ctx.fokus("packdaten", "container") };
      if (schritt.status !== "now" || sid === null) return null;
      if (!rechte.canCreateReceipt) return { art: "hinweis", text: "Zuständig: Lagerleitung" };
      return {
        art: "knopf",
        label: codes.includes("reparatur") ? "Fehlenden Wareneingang sicher anlegen" : "Zustellung bestätigen und Wareneingang anlegen",
        onClick: () => aktionen.oeffneWe(sid),
      };
    case "einlagern":
      // WE abschliessen bzw. Klaerfaelle loesen: Lagerleitung auf der WE-Seite (Link in der Karte).
      if ((schritt.status === "now" || schritt.status === "fix") && !rechte.canCreateReceipt) return { art: "hinweis", text: "Zuständig: Lagerleitung" };
      return null;
    case "bestand":
      if (schritt.status === "gate") return { art: "hinweis", text: GATE_TEXT };
      if (schritt.status !== "now" || sid === null) return null;
      if (!rechte.canBookStock) return { art: "hinweis", text: "Zuständig: Lagerleitung (PIN)" };
      return { art: "knopf", label: "Bestand buchen", onClick: () => aktionen.oeffneBestandBuchen(sid) };
    case "einstandspreis":
      if (schritt.status === "gate") return { art: "hinweis", text: GATE_TEXT };
      // Klaerfall vor Kosten/Kurs: ohne Bestands-Nachweis hilft kein Erfassen (Server lehnt ab).
      if (schritt.status === "fix" && codes.includes("bestand_nicht_gebucht")) return { art: "hinweis", text: KLAERFALL_BESTAND_TEXT };
      if (schritt.status === "fix") {
        if (codes.includes("kosten_erwartet") || codes.includes("kosten_fehlen")) return formular("Kosten erfassen", "kosten");
        return formular("Zahlung erfassen", "zahlung");
      }
      if (schritt.status !== "now" || sid === null) return null;
      if (!rechte.hasPricePerm) return { art: "hinweis", text: "Zuständig: Preise" };
      return {
        art: "knopf", label: "Misch-EK berechnen & freigeben", onClick: () => aktionen.oeffneMischEk(sid),
        ...(rechte.canConfirmMischEk ? {} : { hinweis: ctx.drPushEnabled
          ? "Freigabe braucht zusätzlich das Push-Recht."
          : "Freigabe braucht zusätzlich die Lagerleitung." }),
      };
    case "lieferungen":
      return schritt.status === "done" ? null : { art: "knopf", label: "Restmengen prüfen", onClick: () => ctx.fokus("rest", "positionen") };
    case "invoice":
      return schritt.status === "done" ? null : formular("Rechnungssumme erfassen", "invoice");
    case "zahlungen":
      return schritt.status === "done" ? null : formular("Zahlung erfassen", "zahlung");
    case "kosten":
      return schritt.status === "done" ? null : formular("Kosten erfassen", "kosten");
    default:
      return null;
  }
}

const AVIS_FELDER = [["eta_pod", "ETA Hafen"], ["eta_door", "Zustellung Lager"]] as const;
type AvisFeld = (typeof AVIS_FELDER)[number][0];

/**
 * Avis im Schritt Ankunft (Plan 2026-10-18 P2): ETA Hafen und Zustelldatum Lager direkt hier
 * aendern, damit alle sehen, wann der Container kommt. Derselbe PATCH wie im Reiter Container;
 * gesendet wird nur, was sich geaendert hat (leer = Termin entfernen). Die Historie entsteht
 * serverseitig (eta_change im Verlauf).
 */
function AvisTermine({ shipment, darfAendern }: { shipment: PurchaseShipment; darfAendern: boolean }) {
  const patch = usePatchPurchaseShipment();
  const [entwurf, setEntwurf] = useState<Partial<Record<AvisFeld, string>>>({});
  const [fehler, setFehler] = useState<string | null>(null);
  const geaendert: Partial<Record<AvisFeld, string | null>> = {};
  for (const [feld] of AVIS_FELDER) {
    const v = entwurf[feld];
    if (v !== undefined && v !== (shipment[feld] ?? "")) geaendert[feld] = v === "" ? null : v;
  }
  const hatAenderung = Object.keys(geaendert).length > 0;
  const speichern = () => {
    setFehler(null);
    patch.mutate({ id: shipment.id, body: geaendert }, {
      onSuccess: () => setEntwurf({}),
      onError: (e) => setFehler(e instanceof Error ? e.message : String(e)),
    });
  };
  return (
    <div className="rounded-md border border-line bg-panel px-3 py-2">
      <div className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-ink-2">Avis · wann kommt der Container?</div>
      <div className="flex flex-wrap items-end gap-3">
        {AVIS_FELDER.map(([feld, label]) => (
          <label key={feld} className="text-xs text-ink-2">
            {label}
            <Input
              type="date"
              aria-label={label}
              value={entwurf[feld] ?? shipment[feld] ?? ""}
              disabled={!darfAendern || patch.isPending}
              className="mt-1 h-8 w-40 text-xs"
              onChange={(e) => setEntwurf((d) => ({ ...d, [feld]: e.target.value }))}
            />
          </label>
        ))}
        {darfAendern ? (
          <Button type="button" size="sm" variant="outline" disabled={!hatAenderung || patch.isPending} onClick={speichern}>
            Termine speichern
          </Button>
        ) : null}
      </div>
      {fehler ? <p role="alert" className="mt-1.5 text-xs text-coral">Termine nicht gespeichert: {fehler}</p> : null}
    </div>
  );
}

const ZEILEN_MAX = 10;

/** Soll/Ist je Artikel: bestellt (Akte) · Packliste (Container) · im WE / an DR vorab (Einlagerung, nur spaet und lazy). */
function SollIst({ order, shipment, mitEinlagerung }: { order: PurchaseOrderFile; shipment: PurchaseShipment; mitEinlagerung: boolean }) {
  const { data } = useEinlagerung(mitEinlagerung ? shipment.id : null);
  // Die Rechnung steht je Kopf (stock_item) und nennt ihre Bestellpositionen in orderItemIds.
  // Bei mehreren Positionen desselben Kopfes stehen „im WE“/„an DR“ nur an der ersten Zeile.
  const rechnung = new Map<number, EinlagerungZeile>();
  for (const z of data?.rechnung ?? []) for (const id of z.orderItemIds) rechnung.set(id, z);
  const gezeigteKoepfe = new Set<number>();
  const zeilen = shipment.items.map((si) => {
    const oi = order.items.find((o) => o.id === si.purchase_order_item_id);
    const r = oi ? rechnung.get(oi.id) : undefined;
    const kopfSchonGezeigt = r !== undefined && gezeigteKoepfe.has(r.stockItemId);
    if (r) gezeigteKoepfe.add(r.stockItemId);
    return {
      id: si.id,
      artikel: oi?.merchant_sku ?? oi?.sku ?? (oi ? `Pos. ${oi.position}` : "nicht zugeordnet"),
      stern: oi?.merchant_sku ?? oi?.sku ?? null,
      name: oi?.name_snapshot ?? null,
      einheit: oi?.einheit,
      bestellt: oi?.qty ?? null,
      packliste: si.qty,
      imWe: kopfSchonGezeigt ? null : r?.eingelagert ?? null,
      vorab: kopfSchonGezeigt ? null : r?.vorabGepusht ?? null,
      kopfPositionen: r && !kopfSchonGezeigt ? r.orderItemIds.length : 0,
      kopfSchonGezeigt,
    };
  });
  if (zeilen.length === 0) {
    return <p className="text-xs text-ink-2">Noch keine Packliste: Soll/Ist entsteht, sobald die Positionen des Containers bestätigt sind.</p>;
  }
  const sichtbar = zeilen.slice(0, ZEILEN_MAX);
  const rest = zeilen.length - sichtbar.length;
  const num = (n: number | null, einheit: (typeof zeilen)[number]["einheit"]) => (n === null ? "—" : mengeMitEinheit(n, einheit));
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-[13px]">
        <caption className="sr-only">Soll und Ist je Artikel</caption>
        <thead>
          <tr className="text-left text-[11px] uppercase tracking-wide text-ink-2">
            <th scope="col" className="pb-1 font-semibold">Artikel</th>
            <th scope="col" className="pb-1 text-right font-semibold">bestellt</th>
            <th scope="col" className="pb-1 text-right font-semibold">Packliste</th>
            <th scope="col" className="pb-1 text-right font-semibold">im WE</th>
            <th scope="col" className="pb-1 text-right font-semibold">an DR (vorab)</th>
          </tr>
        </thead>
        <tbody>
          {sichtbar.map((z) => (
            <tr key={z.id} className="border-t border-line-2">
              <td className="py-1 pr-2">
                <span className="font-mono text-ink"><GrundartikelLink code={z.stern}>{z.artikel}</GrundartikelLink></span>
                {z.name ? <span className="ml-2 text-ink-2">{z.name}</span> : null}
              </td>
              <td className="py-1 text-right font-mono">{num(z.bestellt, z.einheit)}</td>
              <td className={`py-1 text-right font-mono ${z.bestellt !== null && z.packliste !== z.bestellt ? "text-warn" : ""}`}>{num(z.packliste, z.einheit)}</td>
              {mitEinlagerung && z.kopfSchonGezeigt ? (
                <td colSpan={2} className="py-1 text-right text-xs text-ink-2">im Artikel oben enthalten</td>
              ) : (
                <>
                  <td className="py-1 text-right font-mono">
                    {mitEinlagerung ? num(z.imWe, z.einheit) : "—"}
                    {mitEinlagerung && z.kopfPositionen > 1 ? (
                      <span className="ml-1 font-sans text-[11px] text-ink-2">({z.kopfPositionen} Positionen)</span>
                    ) : null}
                  </td>
                  <td className="py-1 text-right font-mono">{mitEinlagerung ? num(z.vorab, z.einheit) : "—"}</td>
                </>
              )}
            </tr>
          ))}
        </tbody>
      </table>
      {rest > 0 ? <p className="mt-1 text-xs text-ink-2">+ {rest} weitere Positionen im Reiter Container.</p> : null}
    </div>
  );
}

/**
 * Bestellspur Schritt 1: welche Positionen noch Rest haben (bestellt / verschifft / offen ueber
 * alle Container). Mit Einkaufsrecht wird der Rest hier ausgewiesen (Begruendung, wie im Reiter
 * Positionen) und es lassen sich Positionen hinzufuegen.
 */
function OffeneRestmengen({ order, darf, onPositionen }: { order: PurchaseOrderFile; darf: boolean; onPositionen: () => void }) {
  const offen = (order.restmengen?.positionen ?? []).filter((p) => p.status === "offen");
  const knopf = darf ? (
    <Button type="button" size="sm" variant="outline" className="mt-2" onClick={onPositionen}>Positionen hinzufügen</Button>
  ) : null;
  if (offen.length === 0) return knopf;
  const sichtbar = offen.slice(0, ZEILEN_MAX);
  const rest = offen.length - sichtbar.length;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-[13px]">
        <caption className="sr-only">Offene Restmengen je Position</caption>
        <thead>
          <tr className="text-left text-[11px] uppercase tracking-wide text-ink-2">
            <th scope="col" className="pb-1 font-semibold">Artikel</th>
            <th scope="col" className="pb-1 text-right font-semibold">bestellt</th>
            <th scope="col" className="pb-1 text-right font-semibold">verschifft</th>
            <th scope="col" className="pb-1 text-right font-semibold">offen</th>
            {darf ? <th scope="col" className="pb-1 text-right font-semibold"><span className="sr-only">Aktion</span></th> : null}
          </tr>
        </thead>
        <tbody>
          {sichtbar.map((p, i) => {
            const oi = order.items.find((o) => o.id === p.order_item_id);
            const sku = oi?.merchant_sku ?? oi?.sku ?? `Pos. ${oi?.position ?? "?"}`;
            return (
              <tr key={p.order_item_id} className="border-t border-line-2">
                <td className="py-1 pr-2">
                  <span className="font-mono text-ink"><GrundartikelLink code={oi?.merchant_sku ?? oi?.sku}>{oi?.merchant_sku ?? oi?.sku ?? `Pos. ${oi?.position ?? "?"}`}</GrundartikelLink></span>
                  {oi?.name_snapshot ? <span className="ml-2 text-ink-2">{oi.name_snapshot}</span> : null}
                </td>
                <td className="py-1 text-right font-mono">{mengeMitEinheit(p.bestellt, oi?.einheit)}</td>
                <td className="py-1 text-right font-mono">{mengeMitEinheit(p.verschifft, oi?.einheit)}</td>
                <td className="py-1 text-right font-mono font-semibold text-warn">{mengeMitEinheit(p.offen, oi?.einheit)}</td>
                {darf ? (
                  <td className="py-1 pl-2 text-right">
                    <RestAusweisenKnopf rest={p} einheit={oi?.einheit} sku={sku} fokusZiel={i === 0 ? "rest" : undefined} />
                  </td>
                ) : null}
              </tr>
            );
          })}
        </tbody>
      </table>
      {rest > 0 ? <p className="mt-1 text-xs text-ink-2">+ {rest} weitere Positionen im Reiter Positionen.</p> : null}
      {knopf}
    </div>
  );
}

/** Schritt 1: Packlisten des Containers mit Status; die ausgelesene wird hier geprueft. */
function PacklistenBelege({ belege, darf, onPruefen }: { belege: PurchaseDocument[]; darf: boolean; onPruefen: (id: number) => void }) {
  if (belege.length === 0) {
    return <p className="text-xs text-ink-2">Noch keine Packliste für diesen Container. Hochladen startet die Auswertung; danach hier prüfen und bestätigen.</p>;
  }
  return (
    <ul aria-label="Packlisten dieses Containers" className="grid gap-1 rounded-md border border-line bg-panel px-3 py-2 text-xs">
      {belege.map((d) => {
        const name = d.file_name || `Dokument #${d.id}`;
        return (
          <li key={d.id} className="flex flex-wrap items-center gap-2">
            <span className="min-w-0 flex-1 truncate text-ink">{name}</span>
            <span className="text-ink-2">{docStatusLabel(d.agent_status)}</span>
            {darf && d.agent_status === "bestaetigt" ? (
              <Button type="button" size="sm" variant="ghost" className="h-7 px-2 text-xs" aria-label={`Packliste ${name} ansehen`}
                onClick={() => onPruefen(d.id)}>
                Ansehen
              </Button>
            ) : null}
          </li>
        );
      })}
    </ul>
  );
}

/**
 * Schritt 2 (und Klaerung bei Ankunft): Kartons und Stueck je Karton je Position, so wie sie aus
 * der bestaetigten Packliste kommen. Fehlen sie fachlich, wird eine Ausnahme begruendet (Dialog
 * der Akte); falsche Werte aendert man direkt („Ändern“, Schnitt 4) oder ueber die Packliste.
 * Packstation-Anteil direkt hier.
 */
function Packdaten({ order, shipment, schritt, belege, darf, aktionen }: {
  order: PurchaseOrderFile;
  shipment: PurchaseShipment;
  schritt: IrgendeinSchritt;
  belege: PurchaseDocument[];
  darf: boolean;
  aktionen: AufgabeKarteProps["aktionen"];
}) {
  const [offen, setOffen] = useState<number | null>(null);
  const blockiert = new Set(schritt.codes.flatMap((c) => (c.item_id === undefined ? [] : [c.item_id])));
  const gesperrt = packdatenGesperrt(order, shipment);
  const bestaetigt = belege.filter((d) => d.agent_status === "bestaetigt");
  if (shipment.items.length === 0) {
    return <p className="text-xs text-ink-2">Noch keine Positionen im Container: Packdaten kommen aus der bestätigten Packliste.</p>;
  }
  let erstesZiel = true;
  const ziel = () => {
    const z = erstesZiel ? "packdaten" : undefined;
    erstesZiel = false;
    return z;
  };
  const zeilen = shipment.items.map((si) => {
    const oi = order.items.find((o) => o.id === si.purchase_order_item_id);
    const stern = oi?.merchant_sku ?? oi?.sku ?? `Position #${si.id}`;
    const fehlt = si.cartons == null || si.pieces_per_carton == null;
    return { si, oi, stern, fehlt, klaerung: !si.carton_exception_reason && (fehlt || blockiert.has(si.id)) };
  });
  return (
    <div className="grid gap-3">
      <div className="overflow-x-auto">
        <table className="w-full text-[13px]">
          <caption className="sr-only">Packdaten je Position</caption>
          <thead>
            <tr className="text-left text-[11px] uppercase tracking-wide text-ink-2">
              <th scope="col" className="pb-1 font-semibold">Artikel</th>
              <th scope="col" className="pb-1 text-right font-semibold">Menge</th>
              <th scope="col" className="pb-1 text-right font-semibold">Kartons</th>
              <th scope="col" className="pb-1 text-right font-semibold">je Karton</th>
              <th scope="col" className="pb-1 text-right font-semibold">Stand</th>
            </tr>
          </thead>
          <tbody>
            {zeilen.map(({ si, oi, stern, klaerung }) => (
              <Fragment key={si.id}>
              <tr className="border-t border-line-2 align-top">
                <td className="py-1 pr-2">
                  <span className="font-mono text-ink"><GrundartikelLink code={oi?.merchant_sku ?? oi?.sku}>{stern}</GrundartikelLink></span>
                  {darf && !gesperrt ? (
                    <Button type="button" size="sm" variant="ghost" className="ml-1 h-6 px-1.5 text-[11px]"
                      aria-label={`Packdaten ändern für ${stern}`} aria-expanded={offen === si.id}
                      onClick={() => setOffen(offen === si.id ? null : si.id)}>
                      {offen === si.id ? "Schließen" : "Ändern"}
                    </Button>
                  ) : null}
                </td>
                <td className="py-1 text-right font-mono">{mengeMitEinheit(si.qty, oi?.einheit)}</td>
                <td className={`py-1 text-right font-mono ${si.cartons == null ? "text-warn" : ""}`}>
                  {si.cartons == null ? "fehlt" : `${si.cartons.toLocaleString("de-DE")} Kartons`}
                </td>
                <td className={`py-1 text-right font-mono ${si.pieces_per_carton == null ? "text-warn" : ""}`}>
                  {si.pieces_per_carton == null ? "fehlt" : `${si.pieces_per_carton.toLocaleString("de-DE")} St./Karton`}
                </td>
                <td className="py-1 pl-2 text-right">
                  {si.carton_exception_reason ? (
                    <div className="grid justify-items-end gap-1">
                      <span className="text-[11px] text-warn">{`Ausnahme: „${si.carton_exception_reason}“`}</span>
                      {darf ? (
                        <Button type="button" size="sm" variant="ghost" className="h-7 px-2 text-xs"
                          aria-label={`Ausnahme aufheben für ${stern}`} data-fokus-ziel={ziel()}
                          onClick={() => aktionen.korrektur("clear_carton_exception", si.id)}>
                          Aufheben
                        </Button>
                      ) : null}
                    </div>
                  ) : klaerung ? (
                    darf ? (
                      <Button type="button" size="sm" variant="outline" className="h-7 px-2 text-xs"
                        aria-label={`Ausnahme begründen für ${stern}`} data-fokus-ziel={ziel()}
                        onClick={() => aktionen.korrektur("carton_exception", si.id)}>
                        Ausnahme begründen
                      </Button>
                    ) : <span className="text-xs font-semibold text-warn">Klärung nötig</span>
                  ) : <span className="text-xs text-ink-2">ok</span>}
                </td>
              </tr>
              {offen === si.id && darf && !gesperrt ? (
                <tr>
                  <td colSpan={5} className="pb-2">
                    <PackdatenEingabe key={si.id} item={si} stern={stern} meter={oi?.einheit === "meter"} onFertig={() => setOffen(null)} />
                  </td>
                </tr>
              ) : null}
              </Fragment>
            ))}
          </tbody>
        </table>
      </div>
      {darf && !gesperrt && bestaetigt.length > 0 ? (
        <div className="flex flex-wrap items-center gap-2 text-xs text-ink-2">
          <span>Werte falsch? Packliste mit Begründung wieder zur Prüfung öffnen:</span>
          {bestaetigt.map((d) => {
            const name = d.file_name || `Dokument #${d.id}`;
            return (
              <Button key={d.id} type="button" size="sm" variant="outline" className="h-7 px-2 text-xs"
                aria-label={`Packliste ${name} korrigieren`} data-fokus-ziel={ziel()}
                onClick={() => aktionen.korrektur("reopen_document", d.id)}>
                {name} korrigieren
              </Button>
            );
          })}
        </div>
      ) : null}
      <div className="grid gap-2">
        <div className="text-[11px] font-semibold uppercase tracking-wide text-ink-2">Packstation-Direktanteil je Position</div>
        {zeilen.map(({ si, stern }) => (
          <PackstationAnteil key={si.id} item={si} stern={stern} locked={!darf || gesperrt} />
        ))}
      </div>
    </div>
  );
}

/** Die eine Aufgabe-Karte in der Mitte: Kopf, Voraussetzungen, Soll/Ist, Hauptknopf, „Danach“. */
export function AufgabeKarte({ spur, schritt, shipment, order, titel, farbe, rechte, aktionen, documents, uploadLaeuft }: AufgabeKarteProps) {
  const karte = useRef<HTMLElement>(null);
  const gesamt = spur.schritte.length;
  if (schritt === null) {
    return (
      <section aria-labelledby="aufgabe-titel" className="rounded-md border border-line bg-background p-4" style={{ borderTopColor: farbe, borderTopWidth: 3 }}>
        <h3 id="aufgabe-titel" className="font-display text-base font-bold text-ink">{titel} · Alles erledigt</h3>
        <p className="mt-1 text-sm text-ink-2">
          {spur.typ === "container"
            ? "Für diesen Container ist nichts mehr offen. Die Akte unten bleibt zum Nachschlagen."
            : "Lieferungen, Rechnung, Zahlungen und Kosten sind vollständig."}
        </p>
      </section>
    );
  }

  const text = schrittText(schritt);
  // Hauptknoepfe bleiben im Arbeitsplatz (21.10.2026 Schnitt 2): Fokus auf die erste Aktion in
  // der Karte; nur wenn es dort nichts zu tun gibt, geht es in den Reiter der Akte.
  const fokus: Fokus = (ziel, reiter) => {
    const el = karte.current?.querySelector<HTMLElement>(`[data-fokus-ziel="${ziel}"]`);
    if (!el) return aktionen.zeigeReiter(reiter);
    el.focus();
    el.scrollIntoView?.({ block: "nearest" });
  };
  const aktion = hauptaktion(schritt, shipment, rechte, aktionen, { titel, documents, fokus,
    drPushEnabled: order.arbeitsplatz?.dr_push_enabled !== false });
  const packlisten = shipment ? packlistenVon(documents, shipment.id) : [];
  const mitPackdaten = shipment !== null && (schritt.key === "packdaten" || (schritt.key === "ankunft" && schritt.status === "fix"));
  // Reihenfolge erst Bestand buchen, dann Misch-EK: bei Einlagern/Bestand steht der Grund aus
  // dem Code des Schritts 7 derselben Spur dort, wo gerade gearbeitet wird.
  const mischEkGrund = (schritt.key === "einlagern" || schritt.key === "bestand")
    ? (spur.schritte as IrgendeinSchritt[]).find((s) => s.key === "einstandspreis")?.codes.find((c) => c.code === "bestand_nicht_gebucht")?.code ?? null
    : null;
  const danach = (spur.schritte as IrgendeinSchritt[]).filter((s) => s.nr > schritt.nr && s.status !== "done").slice(0, 2);
  const mitEinlagerung = shipment !== null && shipment.warehouse_receipt_id !== null && schritt.nr >= 5;
  const weLink = shipment?.warehouse_receipt_id !== null && shipment?.warehouse_receipt_id !== undefined
    ? `/warehouse/wareneingang/${shipment.warehouse_receipt_id}` : null;

  return (
    <section
      ref={karte}
      aria-labelledby="aufgabe-titel"
      className="flex flex-col gap-4 rounded-md border border-line bg-background p-4"
      style={{ borderTopColor: farbe, borderTopWidth: 3 }}
    >
      <header className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h3 id="aufgabe-titel" className="font-display text-base font-bold text-ink">{titel} · {text.aufgabe}</h3>
          <p className="text-xs text-ink-2">Schritt {schritt.nr} von {gesamt} · {text.wer}</p>
        </div>
        <span className={`rounded-full border px-2 py-0.5 text-[11px] font-semibold ${statusChipKlasse(schritt.status)}`}>
          {STATUS_TEXTE[schritt.status]}
        </span>
      </header>

      {schritt.codes.length > 0 ? (
        <div className={`rounded-md border px-3 py-2 text-sm ${schritt.status === "fix" ? "border-coral/50 bg-coral-soft" : "border-line bg-panel"}`}>
          <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-ink-2">
            {schritt.status === "fix" ? "Klärung nötig" : "Voraussetzung"}
          </div>
          <ul className="grid gap-0.5 text-ink">
            {schritt.codes.map((c, i) => (
              <li key={`${c.code}-${c.item_id ?? i}`}>
                {codeText(c.code)}
                {c.item_id !== undefined ? <span className="text-ink-2"> (Position {positionsText(order, c.item_id)})</span> : null}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {mischEkGrund ? (
        <p role="note" className="rounded-md border border-line bg-panel px-3 py-2 text-sm text-ink">
          <span className="font-semibold">Danach Misch-EK:</span> {codeText(mischEkGrund)}
        </p>
      ) : null}

      {schritt.key === "ankunft" && schritt.status === "now" && shipment ? (
        <p className="text-sm text-ink">
          Steht <strong className="font-mono">{shipment.container_no ?? shipment.label ?? `#${shipment.id}`}</strong> im Hof?
          Mit der Bestätigung entstehen Zustellung und Wareneingang in einem Schritt.
        </p>
      ) : null}

      {/* 22.10.2026 Befund 6: dieselben Prozessknoepfe wie im Reiter Container (Status-Route braucht Einkaufsrecht). */}
      {shipment && shipment.warehouse_receipt_id === null && schritt.key === "transport" && rechte.canEditAkte ? (
        <ContainerStatusAktionen shipment={shipment} darfKorrigieren={rechte.hasPricePerm} gesperrt={false} />
      ) : null}

      {shipment && shipment.warehouse_receipt_id === null && (schritt.key === "transport" || schritt.key === "ankunft") ? (
        <AvisTermine key={shipment.id} shipment={shipment} darfAendern={rechte.canEditAkte} />
      ) : null}

      {shipment && schritt.key === "packliste" ? (
        <PacklistenBelege belege={packlisten} darf={rechte.canEditAkte} onPruefen={aktionen.pruefeBeleg} />
      ) : null}

      {shipment ? <SollIst order={order} shipment={shipment} mitEinlagerung={mitEinlagerung} /> : null}

      {mitPackdaten && shipment ? (
        <Packdaten order={order} shipment={shipment} schritt={schritt} belege={packlisten} darf={rechte.canEditAkte} aktionen={aktionen} />
      ) : null}

      {schritt.key === "lieferungen" && schritt.status !== "done" ? (
        <OffeneRestmengen order={order} darf={rechte.canEditAkte && order.status !== "entwurf"} onPositionen={aktionen.oeffnePositionen} />
      ) : null}

      {schritt.key === "einlagern" && weLink ? (
        <p className="text-sm">
          <Link to={weLink} className="font-semibold text-primary hover:underline">Zum Wareneingang →</Link>
          <span className="ml-2 text-ink-2">{weHinweis(schritt.codes.map((c) => c.code))}</span>
        </p>
      ) : null}

      <footer className="mt-auto flex flex-wrap items-center gap-3 border-t border-line-2 pt-3">
        {aktion?.art === "knopf" ? (
          <div className="flex flex-col gap-1">
            <Button type="button" onClick={aktion.onClick} className="font-semibold">{aktion.label}</Button>
            {aktion.hinweis ? <span className="text-xs text-ink-2">{aktion.hinweis}</span> : null}
          </div>
        ) : aktion?.art === "upload" ? (
          <BelegHochladen hauptknopf zielText={titel} knopfText={aktion.label} feldLabel={aktion.feldLabel} laeuft={uploadLaeuft}
            onDateien={(d) => aktionen.belegeHochladen(d, aktion.shipmentId)} />
        ) : aktion?.art === "hinweis" ? (
          <span role="status" className="text-sm font-semibold text-ink-2">{aktion.text}</span>
        ) : schritt.status === "wait" ? (
          <span role="status" className="text-sm text-ink-2">Wartet · {codeText(schritt.codes[0]?.code ?? "")}</span>
        ) : null}
        {rechte.canEditAkte && aktion?.art !== "upload" ? (
          <BelegHochladen zielText={titel} laeuft={uploadLaeuft} onDateien={(d) => aktionen.belegeHochladen(d, shipment?.id ?? null)} />
        ) : null}
        {danach.length > 0 ? (
          <span className="ml-auto text-xs text-ink-2">
            Danach: {danach.map((s) => schrittText(s).kurz).join(" → ")}
          </span>
        ) : null}
      </footer>
    </section>
  );
}

/** Schritt 5 (Plan 2026-10-19 a0/e): was auf der WE-Seite zu tun ist. */
function weHinweis(codes: string[]): string {
  if (codes.includes("we_abschliessen")) return "Dort „Wareneingang abschließen“ (Lagerleitung).";
  if (codes.includes("we_klaerfaelle_offen")) return "Dort die Klärfälle mit Begründung lösen (Lagerleitung).";
  if (codes.includes("we_unvollstaendig")) return "Dort je Position „Ist übernehmen“ oder stornieren; die Scan-App erfasst den Rest.";
  return "Das Lager erfasst mit der Scan-App; hier gibt es nichts zu klicken.";
}

function positionsText(order: PurchaseOrderFile, itemId: number): string {
  // item_id aus dem Workflow ist die Container-Position; ihr Bestell-Artikel steht in der Akte.
  for (const s of order.shipments) {
    const si = s.items.find((i) => i.id === itemId);
    if (si) {
      const oi = order.items.find((o) => o.id === si.purchase_order_item_id);
      return oi?.merchant_sku ?? oi?.sku ?? `#${itemId}`;
    }
  }
  return `#${itemId}`;
}

export type { ArbeitsplatzSchrittKey };
